import React, { useRef } from "react";
import Label from "../label/label";
import Togglebox from "../togglebox/togglebox";
import { Field, useFormikContext } from "formik";
import useFocusOnRouteChange from "../../hooks/useFocusOnRouteChange";
import PaginatedSearchableSelectField from "../selection-field/paginated-searchable-select-field";
import { getPasswordMonths, getPasswordRemembered } from "../../services/securityPolicyService";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";

export const SecurityPolicyPassword = () => {
    const { values, setFieldValue } = useFormikContext<any>();
    const inputRef = useRef<HTMLInputElement>(null);
    const { translate } = useLocale();
    useFocusOnRouteChange(inputRef);

    return (
        <div className="space-y-6 max-w-2xl">
            <h2 className="text-xl text-blue-600 font-semibold">
                {translate.byKey(TranslationKeys.securityPolicy)}
            </h2>

            <div className="grid grid-cols-2 gap-4">
                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforceUppercase)}:`} name="enforceUppercase" />
                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforceLowercase)}:`} name="enforceLowercase" />

                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforceDigit)}:`} name="enforceDigits" />
                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforceSpecialCharacter)}:`} name="enforceSpecialCharacters" />

                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforcePasswordLifetime)}:`} name="enforcePasswordLifetime" />
                <div className="flex flex-col">
                    <Label htmlFor="passwordLifetime" className="text-sm text-gray-600">
                        {translate.byKey(TranslationKeys.months)}:
                    </Label>
                    <div className="w-64">
                        <PaginatedSearchableSelectField
                            name="passwordLifetime"
                            key="passwordLifetime"
                            accessor="passwordLifetime"
                            data-cy="passwordLifetime"
                            dataMethod={getPasswordMonths()}
                            searchAccessor="label"
                            onSelect={(item) => setFieldValue("passwordLifetime", item)}
                            selectedItemLabel={values.passwordLifetime !== undefined ? values.passwordLifetime.toString() : "0"}
                        />
                    </div>
                </div>

                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforceMinimumLength)}:`} name="enforcePasswordMinLength" />
                <div className="flex flex-col">
                    <Label htmlFor="minLength" className="text-sm text-gray-600">
                        {translate.byKey(TranslationKeys.minimumLength)}:
                    </Label>
                    <Field
                        type="text"
                        name="passwordMinLength"
                        data-cy="passwordMinLength"
                        innerRef={inputRef}
                        className="bg-gray-100 pl-2 w-64"
                        id="nameInput"
                    />
                </div>

                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforcePasswordHistory)}:`} name="enforcePasswordHistory" />
                <div className="flex flex-col">
                    <Label htmlFor="passwordHistoryNumber" className="text-sm text-gray-600">
                        {translate.byKey(TranslationKeys.passwordsRemembered)}:
                    </Label>
                    <div className="w-64">
                        <PaginatedSearchableSelectField
                            name="passwordHistoryNumber"
                            key="passwordHistoryNumber"
                            accessor="passwordHistoryNumber"
                            data-cy="passwordHistoryNumber"
                            dataMethod={getPasswordRemembered()}
                            searchAccessor="label"
                            onSelect={(item) => setFieldValue("passwordHistoryNumber", item)}
                            selectedItemLabel={values.passwordHistoryNumber !== undefined ? values.passwordHistoryNumber.toString() : "1"}
                        />
                    </div>
                </div>

                <PolicyToggle label={`${translate.byKey(TranslationKeys.enforceFirstPasswordChange)}:`} name="enforceInitialPasswordChange" />
            </div>
        </div>
    );
};

const PolicyToggle = ({ label, name }) => (
    <div className="flex flex-col gap-1">
        <Label htmlFor={name} className="text-sm text-gray-600 ">{label}</Label>
        <Togglebox name={name} label={""} />
    </div>
);
